import { EditorState, PluginKey } from '@tiptap/pm/state';
import { isInTable } from '@tiptap/pm/tables';
import { EditorView } from '@tiptap/pm/view';
import { Editor, findParentNode, useEditorState } from '@tiptap/react';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconLabel, IconLabelHintDirection } from '../../../../IconLabel';

//eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import { BubbleMenu } from '@tiptap/react/menus';

import classNames from 'classnames';
import classes from './TableMenu.module.scss';

const TABLE_MENU_PLUGIN_KEY = new PluginKey('tableMenu');

interface ShouldShowArgs {
  element: HTMLElement;
  view: EditorView;
  state: EditorState;
}

interface TableMenuProps {
  editor: Editor;
}

const findTable = findParentNode(({ type }) => type.name === 'table');

const shouldShow = ({ element, view, state }: ShouldShowArgs) =>
  (view.hasFocus() || element.contains(document.activeElement)) && isInTable(state);

export const TableMenu: FunctionComponent<TableMenuProps> = ({ editor }) => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'richTextEditor' });

  const { rows, columns, hasHeaderRow } = useEditorState({
    editor,
    selector: ({ editor }) => {
      const table = findTable(editor.state.selection)?.node;

      return {
        rows: table?.childCount ?? 0,
        columns: table?.firstChild?.childCount ?? 0,
        hasHeaderRow: table?.firstChild?.firstChild?.type.name === 'tableHeader',
      };
    },
  });

  const getReferencedVirtualElement = useCallback(() => {
    const table = findTable(editor.state.selection);

    if (!table) {
      return null;
    }

    const tableWrapper = editor.view.nodeDOM(table.pos) as HTMLElement | null;

    const tableElement = tableWrapper?.querySelector('table');

    if (!tableElement) {
      return null;
    }

    return {
      contextElement: tableElement,
      getBoundingClientRect: () => tableElement.getBoundingClientRect(),
      getClientRects: () => [tableElement.getBoundingClientRect()],
    };
  }, [editor]);

  const options = useMemo(
    () => ({
      placement: 'top-start',
      shift: { padding: 8, crossAxis: true },
      hide: true,
      get scrollTarget() {
        return editor.view.dom.parentElement ?? window;
      },
    }),
    [editor]
  );

  const hasSingleRow = rows <= 1;

  const hasSingleColumn = columns <= 1;

  return (
    <BubbleMenu
      editor={editor}
      pluginKey={TABLE_MENU_PLUGIN_KEY}
      shouldShow={shouldShow}
      getReferencedVirtualElement={getReferencedVirtualElement}
      options={options}
      className={classes['table-menu']}
    >
      <IconLabel
        iconId={'table-header-row'}
        iconSize={16}
        data={ctxT('table-toggle-header-row')}
        hintDirection={IconLabelHintDirection.bottomLeft}
        onClick={() => editor.chain().focus().toggleHeaderRow().run()}
        disabled={hasSingleRow}
        className={classNames(classes['table-menu__item'], {
          [classes['table-menu__item--active']]: hasHeaderRow,
          [classes['table-menu__item--disabled']]: hasSingleRow,
        })}
      />

      <span className={classes['table-menu__separator']} />

      <IconLabel
        iconId={'table-row-add'}
        iconSize={16}
        data={ctxT('table-add-row')}
        hintDirection={IconLabelHintDirection.bottom}
        onClick={() => editor.chain().focus().addRowAfter().run()}
        className={classes['table-menu__item']}
      />

      <IconLabel
        iconId={'table-row-delete'}
        iconSize={16}
        data={ctxT('table-delete-row')}
        hintDirection={IconLabelHintDirection.bottom}
        onClick={() => editor.chain().focus().deleteRow().run()}
        disabled={hasSingleRow}
        className={classNames(classes['table-menu__item'], classes['table-menu__item--danger'], {
          [classes['table-menu__item--disabled']]: hasSingleRow,
        })}
      />

      <span className={classes['table-menu__separator']} />

      <IconLabel
        iconId={'table-column-add'}
        iconSize={16}
        data={ctxT('table-add-column')}
        hintDirection={IconLabelHintDirection.bottom}
        onClick={() => editor.chain().focus().addColumnAfter().run()}
        className={classes['table-menu__item']}
      />

      <IconLabel
        iconId={'table-column-delete'}
        iconSize={16}
        data={ctxT('table-delete-column')}
        hintDirection={IconLabelHintDirection.bottom}
        onClick={() => editor.chain().focus().deleteColumn().run()}
        disabled={hasSingleColumn}
        className={classNames(classes['table-menu__item'], classes['table-menu__item--danger'], {
          [classes['table-menu__item--disabled']]: hasSingleColumn,
        })}
      />

      <span className={classes['table-menu__separator']} />

      <IconLabel
        iconId={'delete'}
        iconSize={16}
        data={ctxT('table-delete-table')}
        hintDirection={IconLabelHintDirection.bottom}
        onClick={() => editor.chain().focus().deleteTable().run()}
        className={classNames(classes['table-menu__item'], classes['table-menu__item--danger'])}
      />
    </BubbleMenu>
  );
};
